import { createUseStyles } from 'react-jss';
import { ButtonVariants, ButtonElementTypes } from '../types/index.js';
import { Timings } from '../../../../foundation/timings/timings/index.js';
import { Easing } from '../../../../foundation/easing/easing/index.js';
import { States } from '../../../../foundation/states/states/index.js';
import { canTouch, canHover } from '../../../../foundation/styleHelpers/hoverTouch/hoverTouch/index.js';
import { CornerRadius } from '../../../../foundation/cornerRadius/cornerRadius/index.js';
import { BreakPointsMedia } from '../../../../foundation/responsive/responsive/index.js';

const defineBackgroundColor = (variant, defferColor) => ((variant === ButtonVariants.Outline || variant === ButtonVariants.Text) ? 'transparent' : defferColor);
const defineBorderColor = (variant, defferColor) => ((variant === ButtonVariants.Outline) ? defferColor : undefined);
const defineColor = (variant, textColor, baseColor) => ((variant === ButtonVariants.Filled) ? textColor : baseColor);
const useSkinStyles = createUseStyles({
    '@keyframes rotate': {
        to: {
            transform: 'rotate(360deg)',
        },
    },
    default: {
        backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.base),
        borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.base),
        color: ({ skin, variant, isLoading }) => (isLoading ? 'transparent' : defineColor(variant, skin.text, skin.base)),
        borderStyle: ({ variant }) => (variant === ButtonVariants.Outline) && 'solid',
    },
    interactive: {
        [canHover]: {
            '&:hover': {
                backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.baseAction),
                borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.baseAction),
                color: ({ skin, variant }) => defineColor(variant, skin.textAction, skin.baseAction),
            },
        },
        [canTouch]: {
            '&:active': {
                backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.baseAction),
                borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.baseAction),
                color: ({ skin, variant }) => defineColor(variant, skin.textAction, skin.baseAction),
            },
        },
    },
    active: {
        backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.baseActive),
        borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.baseActive),
        color: ({ skin, variant }) => defineColor(variant, skin.textActive, skin.baseActive),
    },
    activeInteraction: {
        [canHover]: {
            '&:hover': {
                backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.baseActiveAction),
                borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.baseActiveAction),
                color: ({ skin, variant }) => defineColor(variant, skin.textAction, skin.baseActiveAction),
            },
        },
        [canTouch]: {
            '&:active': {
                backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.baseActiveAction),
                borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.baseActiveAction),
                color: ({ skin, variant }) => defineColor(variant, skin.textAction, skin.baseActiveAction),
            },
        },
    },
    disabled: {
        opacity: States.disabled,
        pointerEvents: 'none',
    },
    loadingIcon: {
        color: ({ skin, variant }) => defineColor(variant, skin.text, skin.base),
        animation: `$rotate ${Timings.long} ${Easing.linear} infinite`,
    },
}, {
    name: 'button',
});
const useLayoutStyles = createUseStyles({
    root: {
        display: 'inline-flex',
        verticalAlign: 'top',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: '100%',
        border: ({ Tag, variant }) => (Tag === ButtonElementTypes.Button && variant !== ButtonVariants.Outline) && 'none',
        width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.Default?.value?.blockHeight : null),
        minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.Default?.value?.blockHeight : 'initial'),
        height: ({ sizes }) => sizes.Default.value?.blockHeight,
        padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.Default.value?.paddingVertical} ${sizes.Default.value?.paddingHorizontal}`,
        borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? CornerRadius.circle : sizes.Default.value?.borderRadiusButtonElement),
        borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.Default.value?.borderWidth,
        columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.Default.value?.spaceBetween,
        fontWeight: ({ sizes }) => sizes.Default.value?.fontWeightButtonElements,
        fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.Default.value?.fontSize),
        lineHeight: ({ sizes }) => sizes?.Default.value?.lineHeight,
        transitionProperty: 'background-color, border, color',
        transitionTimingFunction: Easing.ease,
        transitionDuration: Timings.default,
        cursor: ({ Tag, isDisabled, isLoading }) => (isLoading ? 'default' : isDisabled ? 'not-allowed' : Tag !== ButtonElementTypes.Link && 'pointer'),
        [BreakPointsMedia.XXL]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.XXL?.value?.blockHeight : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.XXL?.value?.blockHeight : 'initial'),
            height: ({ sizes }) => sizes.XXL.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes.XXL.value?.paddingVertical} ${sizes.XXL.value?.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? CornerRadius.circle : sizes.XXL.value?.borderRadiusButtonElement),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.XXL.value?.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.XXL.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.XXL.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.XXL.value?.lineHeight,
        },
        [BreakPointsMedia.XL]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.XL?.value?.blockHeight : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.XL?.value?.blockHeight : 'initial'),
            height: ({ sizes }) => sizes.XL.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes.XL.value?.paddingVertical} ${sizes.XL.value?.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? CornerRadius.circle : sizes.XL.value?.borderRadiusButtonElement),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.XL.value?.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.XL.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.XL.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.XL.value?.lineHeight,
        },
        [BreakPointsMedia.LG]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.LG?.value?.blockHeight : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.LG?.value?.blockHeight : 'initial'),
            height: ({ sizes }) => sizes.LG.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes.LG.value?.paddingVertical} ${sizes.LG.value?.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? CornerRadius.circle : sizes.LG.value?.borderRadiusButtonElement),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.LG.value?.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.LG.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.LG.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.LG.value?.lineHeight,
        },
        [BreakPointsMedia.MD]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.MD?.value?.blockHeight : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.MD?.value?.blockHeight : 'initial'),
            height: ({ sizes }) => sizes.MD.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes.MD.value?.paddingVertical} ${sizes.MD.value?.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? CornerRadius.circle : sizes.MD.value?.borderRadiusButtonElement),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.MD.value?.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.MD.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.MD.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.MD.value?.lineHeight,
        },
        [BreakPointsMedia.SM]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.SM?.value?.blockHeight : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.SM?.value?.blockHeight : 'initial'),
            height: ({ sizes }) => sizes.SM.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.SM.value?.paddingVertical} ${sizes.SM.value?.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? CornerRadius.circle : sizes.SM.value?.borderRadiusButtonElement),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.SM.value?.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.SM.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.SM.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.SM.value?.lineHeight,
        },
        [BreakPointsMedia.XS]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.XS?.value?.blockHeight : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.XS?.value?.blockHeight : 'initial'),
            height: ({ sizes }) => sizes.XS.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XS.value?.paddingVertical} ${sizes.XS.value?.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? CornerRadius.circle : sizes.XS.value?.borderRadiusButtonElement),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.XS.value?.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.XS.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.XS.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.XS.value?.lineHeight,
        },
        [BreakPointsMedia.XXS]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.XXS?.value?.blockHeight : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.XXS?.value?.blockHeight : 'initial'),
            height: ({ sizes }) => sizes.XXS.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes.XXS.value?.paddingVertical} ${sizes.XXS.value?.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? CornerRadius.circle : sizes.XXS.value?.borderRadiusButtonElement),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.XXS.value?.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.XXS.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.XXS.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.XXS.value?.lineHeight,
        },
    },
    icon: {
        display: 'inline-block',
        flex: ({ sizes }) => `0 0 ${sizes.Default.value?.iconWidth}px`,
        width: ({ sizes }) => sizes.Default.value?.iconWidth,
        height: ({ sizes }) => sizes.Default.value?.iconHeight,
        [BreakPointsMedia.XXL]: {
            flex: ({ sizes }) => `0 0 ${sizes.XXL.value?.iconWidth}px`,
            width: ({ sizes }) => sizes.XXL.value?.iconWidth,
            height: ({ sizes }) => sizes.XXL.value?.iconHeight,
        },
        [BreakPointsMedia.XL]: {
            flex: ({ sizes }) => `0 0 ${sizes.XL.value?.iconWidth}px`,
            width: ({ sizes }) => sizes.XL.value?.iconWidth,
            height: ({ sizes }) => sizes.XL.value?.iconHeight,
        },
        [BreakPointsMedia.LG]: {
            flex: ({ sizes }) => `0 0 ${sizes.LG.value?.iconWidth}px`,
            width: ({ sizes }) => sizes.LG.value?.iconWidth,
            height: ({ sizes }) => sizes.LG.value?.iconHeight,
        },
        [BreakPointsMedia.MD]: {
            flex: ({ sizes }) => `0 0 ${sizes.MD.value?.iconWidth}px`,
            width: ({ sizes }) => sizes.MD.value?.iconWidth,
            height: ({ sizes }) => sizes.MD.value?.iconHeight,
        },
        [BreakPointsMedia.SM]: {
            flex: ({ sizes }) => `0 0 ${sizes.SM.value?.iconWidth}px`,
            width: ({ sizes }) => sizes.SM.value?.iconWidth,
            height: ({ sizes }) => sizes.SM.value?.iconHeight,
        },
        [BreakPointsMedia.XS]: {
            flex: ({ sizes }) => `0 0 ${sizes.XS.value?.iconWidth}px`,
            width: ({ sizes }) => sizes.XS.value?.iconWidth,
            height: ({ sizes }) => sizes.XS.value?.iconHeight,
        },
        [BreakPointsMedia.XXS]: {
            flex: ({ sizes }) => `0 0 ${sizes.XXS.value?.iconWidth}px`,
            width: ({ sizes }) => sizes.XXS.value?.iconWidth,
            height: ({ sizes }) => sizes.XXS.value?.iconHeight,
        },
        '& > path': {
            '&:not([fill])': {
                fill: 'currentColor',
            },
        },
    },
    content: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        maxWidth: '100%',
        textOverflow: 'ellipsis',
        display: 'block',
        height: ({ sizes }) => sizes?.Default.value?.contentHeight,
        padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.Default.value?.textPaddingVertical} ${sizes?.Default.value?.textPaddingHorizontal}`,
        [BreakPointsMedia.XXL]: {
            height: ({ sizes }) => sizes?.XXL.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XXL.value?.textPaddingVertical} ${sizes?.XXL.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.XL]: {
            height: ({ sizes }) => sizes?.XL.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XL.value?.textPaddingVertical} ${sizes?.XL.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.LG]: {
            height: ({ sizes }) => sizes?.LG.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.LG.value?.textPaddingVertical} ${sizes?.LG.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.MD]: {
            height: ({ sizes }) => sizes?.MD.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.MD.value?.textPaddingVertical} ${sizes?.MD.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.SM]: {
            height: ({ sizes }) => sizes?.SM.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.SM.value?.textPaddingVertical} ${sizes?.SM.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.XS]: {
            height: ({ sizes }) => sizes?.XS.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XS.value?.textPaddingVertical} ${sizes?.XS.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.XXS]: {
            height: ({ sizes }) => sizes?.XXS.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XXS.value?.textPaddingVertical} ${sizes?.XXS.value?.textPaddingHorizontal}`,
        },
    },
    control: {
        display: 'none',
    },
    loading: {
        color: 'transparent',
        position: 'relative',
        pointerEvents: 'none',
    },
    loadingIcon: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        margin: 'auto',
        pointerEvents: 'none',
    },
}, {
    name: 'button-skin',
});

export { useLayoutStyles, useSkinStyles };
